import Vue from 'vue';
import { cloneDeep } from 'lodash';
import { i18n } from '@/plugins/i18n';
import * as types from './mutation-types';
import { capitalizeFirstLetter, decodeHTML, getRandomColor } from '@/utils/main';
import { getRelativeRange, VHUB_NUMBER_OF_CALCULATED_PERIODS, VHUB_TOTAL_NUMBER_OF_PERIODS } from '@/utils/vhub-utils';
import { VitalityCategory } from '@/utils/classes/VitalityCategory';
import { VitalityLoadingState } from '@/utils/classes/VitalityLoadingState';
import { LSK_HIDE_VH_CRITERION_SENSEI, LSK_HIDE_VH_WELCOME } from '@/utils/constants';

const VLS = VitalityLoadingState;

/**
 * To call when one data type has been refreshed
 * @param  {String} what - type of data that was refreshed
 */
function updateRefreshState(state, key, loadingState) {
  if (!state.loading[key]) {
    const tr = i18n.t(`message.VH_WHAT_${key.toUpperCase()}`);
    Vue.set(state.loading, key, {
      key,
      trParams: {
        what: tr,
        capWhat: capitalizeFirstLetter(tr),
      },
      loading: loadingState,
    });
  } else {
    Vue.set(state.loading[key], 'loading', loadingState);
  }
}


/**
 * Teams can be grouped into set of teams to visualize the data of multiple teams
 * @return {Array} Array of objects containing the score keys of Vitality criteria
 */
function makeTeamGroups(state, store) {
  // Filter teams
  const flatTree = store.rootGetters.teamsTreeCurrentFlattened;
  const team = flatTree.find(t => t.id === store.rootState.currentTeam.id);
  let teams = team ? [team] : [];
  if (team && team.users && team.users.length) {
    const userIds = team.users.map(user => user.id);
    teams = [...teams, ...flatTree.filter(t => t.accesses.some(userId => userIds.includes(userId)))];
  }
  const { dataPerTeam } = state;
  const groupByKey = 'parentId0';

  // Group teams
  const groupById = {};
  teams.forEach((team) => {
    const noGroupKey = team[groupByKey] === undefined;
    const strTeamId = `${noGroupKey ? team.id : team[groupByKey]}`;
    const key = noGroupKey ? `not-grouped-${team.id}` : `grouped-${team[groupByKey]}`;
    if (!groupById[key]) {
      const groupByTeam = flatTree.find(team => `${team.id}` === strTeamId);
      groupById[key] = {
        teams: [],
        teamIds: [],
        id: groupByTeam.id,
        key: `${noGroupKey ? groupByTeam.id : `grouped-${groupByTeam.id}`}`,
        name: decodeHTML(groupByTeam.name),
        noGroupKey,
      };
    }
    // Prevent duplicates
    if (!groupById[key].teamIds.includes(team.id)) {
      groupById[key].teams.push(team);
      groupById[key].teamIds.push(team.id);
    }
  });
  const groups = Object.values(groupById);

  for (let i = 0, len = groups.length; i < len; i++) {
    const group = groups[i];
    if (!group.noGroupKey) {
      const teamsLen = group.teams.length;
      group.title = group.teams.map(team => decodeHTML(team.name)).join(', ');
      group.name = `${i18n.t('message.VH_DEFAULT_TEAMS_MANAGED', { name: group.name })} (${teamsLen})`;
    }
    group.chartColor = getRandomColor();
  }
  return groups;
}

/** Set state information about the current loading of a group */
function setGroupLoadingData(state, payload) {
  if (payload.loadingKey) {
    if (payload.ids) {
      payload.ids.forEach((id) => {
        const lData = state.loadingPerGroup[`${id}`] || {};
        lData[payload.loadingKey] = payload.loadingValue;
        Vue.set(state.loadingPerGroup, `${id}`, lData);
      });
    } else if (payload.id) {
      const lData = state.loadingPerGroup[`${payload.id}`] || {};
      lData[payload.loadingKey] = payload.loadingValue;
      Vue.set(state.loadingPerGroup, `${payload.id}`, lData);
    }
  }
}

/**
 * Calculate scores for all groups by looping through the criteria of the category and calling 'scores' func
 *
 * @param  {Object} state
 * @param  {Object} category - One of VitalityCategory.all
 * @param  {Boolean} [onlyIfNeedsUpdate=false] - If true, only update the scores of the groups which
 *                                               have 'needsUpdate' set to true. Else, update all the groups
 */
function calculateCategoryScores(state, category, onlyIfNeedsUpdate = false) {
  const { teamGroups, ranges, dataPerTeam, allData, allTeamsObject } = state;
  const { key, criteria, scoresKey, scoreDeltaKey, dataInfo } = category;
  const groups = cloneDeep(teamGroups);
  const aTeamsObject = cloneDeep(allTeamsObject);
  // ========================
  // Calculate scores for each team group
  for (let i = 0, len = groups.length; i < len; i++) {
    const group = groups[i];
    if (onlyIfNeedsUpdate && !group.needsUpdate) {
      // This group doesn't need to be updated
      continue;
    }
    // Group's total score for this category (score for the current period and last period)
    let totalCatScores = [];
    let totalCatWeight = 0;
    // Concat data of all the teams in this group
    const data = {};
    dataInfo.forEach(({ specDataKey }) => {
      data[specDataKey] = group.teams.reduce((allGroupData, t) => [
        ...allGroupData,
        ...((dataPerTeam[`${t.id}`] && dataPerTeam[`${t.id}`][key][specDataKey]) || [])
      ], []);
    });
    // Each criterion has a different way of calculating the score
    criteria.forEach((crit) => {
      const tmpBench = Date.now();

      // Scores for the criterion, 1 score per period
      const scores = crit.scores(data, ranges, VHUB_NUMBER_OF_CALCULATED_PERIODS, group.teams);
      group[crit.scoresKey] = scores
      // Vue.set(group, crit.scoresKey, scores);

      // Update total category scores
      totalCatScores = scores.map(({ score }, i) => (totalCatScores[i] || 0) + score * crit.weight);
      totalCatWeight += crit.weight;
    });
    // Scores for the category
    // Vue.set(group, scoresKey, totalCatScores.map(score => score / (totalCatWeight || 1)));
    group[scoresKey] =  totalCatScores.map(score => score / (totalCatWeight || 1));
  }
  state.teamGroups = groups;


  // ========================
  // Calculate the same criteria scores for 'allTeamsObject'.
  // The scores are based on data from all the teams at once
  const data = allData[key];
  if (data) {
    // Score for this category
    let totalScores = [];
    let totalWeight = 0;
    criteria.forEach((crit) => {
      // Scores for the crit, 1 score per period
      const scoresData = crit.scores(data, ranges, 2);
      // Vue.set(aTeamsObject, crit.scoresKey, scoresData);
      aTeamsObject[crit.scoresKey] = scoresData;
      totalScores = scoresData.map(({ score }, i) => (totalScores[i] || 0) + score * crit.weight);
      totalWeight += crit.weight;
    });
    // Vue.set(aTeamsObject, scoresKey, totalScores.map(score => score / (totalWeight || 1)));
    aTeamsObject[scoresKey] = totalScores.map(score => score / (totalWeight || 1));
  }
  state.allTeamsObject = aTeamsObject;

}


const mutations = {

  /** Set the VitalityGroups from the server */
  [types.SET_GROUPS](state, payload) {
    const { store, data } = payload;

    // Keep data of previous groups in case new groups re-use the data
    const prevTeamGroups = {};
    state.teamGroups.forEach((group) => {
      const key = group.teams.map(t => t.id).sort().join(',');
      prevTeamGroups[key] = group;
    });

    // If the team hasn't defined its groups yet, sing default groups
    state.usingDefaultTeamGroups = !(data instanceof Array && data.length > 0);
    const newTeamGroups = state.usingDefaultTeamGroups
      ? makeTeamGroups(state, store)
      : data.map(group => ({ ...group, key: `${group.id}`, chartColor: getRandomColor() }));

    // Check if nex groups can re-use the previous data without refresh
    newTeamGroups.forEach((group, idx, groups) => {
      const key = group.teams.map(t => t.id).sort().join(',');
      if (prevTeamGroups[key]) {
        group.needsUpdate = false;
        groups[idx] = { ...prevTeamGroups[key], ...group };
      } else {
        group.needsUpdate = true;
      }
    });

    state.teamGroups = newTeamGroups;
  },

  /** Set the ranges chosen by the user into state */
  [types.SET_RANGE](state, payload) {
    if (payload && payload.start && payload.end) {
      const ranges = [payload];
      for (let i = 1; i < VHUB_TOTAL_NUMBER_OF_PERIODS; i++) {
        ranges.push(getRelativeRange(payload, -i));
      }
      Vue.set(state, 'ranges', ranges);
    }
  },

  /**
   * New set of data has been fetched from the server.
   * Segregate the data per teams and calculate the vitality of each criterion and each category
   *
   * @param {Object} payload - Should contain a 'category' and 'data' attributes.
   *                           Refer to VitalityCategory/index.js for more informations
   */
  [types.SET_DATA](state, payload) {
    const { category, data, specDataKey, dataTeamKey } = payload;
    // Initialize array
    const bench = Date.now();
    const dPerTeam = state.dataPerTeam;
    if (payload.params.teams) {
      payload.params.teams.forEach((teamId) => {
        if (!dPerTeam[`${teamId}`]) {
          dPerTeam[`${teamId}`] = {};
        }
        if (!dPerTeam[`${teamId}`][category.key]) {
          dPerTeam[`${teamId}`][category.key] = {};
        }
        const tmpbench = Date.now();
        const dd = data.filter(item => item[dataTeamKey] === teamId);
        dPerTeam[`${teamId}`][category.key][specDataKey] = dd;
      });
    }
    state.dataPerTeam = dPerTeam;

    // Also assign the entire data to allData to calculate global averages
    // per category and criterion
    const allData = state.allData;
    if (!allData[category.key]) {
      state.allData[category.key] = {};
    }
    state.allData[category.key][specDataKey] = data;
    state.allData = allData;
  },

  /**
   * Calculate the scores of each criterion of one or several category
   */
  [types.CALCULATE_GROUP_SCORES](state, payload) {
    if (payload.category) {
      if (payload.category instanceof Array) {
        payload.category.forEach((category) => {
          calculateCategoryScores(state, category, payload.onlyIfNeedsUpdate);
        });
      } else {
        calculateCategoryScores(state, payload.category, payload.onlyIfNeedsUpdate);
      }
    }
  },

  /**
   * Calculate the overall vitality score for each group according to the criteria scores
   * which were calculated in CALCULATE_GROUP_SCORES
   */
  [types.CALCULATE_ALL_GROUPS_VITALITY](state) {
    const { teamGroups, ranges, dataPerTeam } = state;
    const jlen = VitalityCategory.all.length;
    for (let i = 0, len = teamGroups.length; i < len; i++) {
      const group = teamGroups[i];
      // Total vitality score for this team (score for the current period and last period)
      let totalVitScores = [];
      let totalVitWeight = 0;

      for (let j = 0; j < jlen; j++) {
        const { key, criteria, scoresKey, scoreDeltaKey } = VitalityCategory.all[j];
        let totalCatScores = [];
        let totalCatWeight = 0;

        criteria.forEach((crit) => {
          totalCatScores = group[crit.scoresKey]
            .map(({ score }, i) => (totalCatScores[i] || 0) + ((score || 0) * crit.weight));
          totalCatWeight += crit.weight;
        });

        // Update total vitality score for this team
        totalVitScores = totalCatScores.map((score, i) => score + (totalVitScores[i] || 0));
        totalVitWeight += totalCatWeight;
      }
      Vue.set(group, 'vitalityScores', totalVitScores.map(score => score / totalVitWeight));
      // group.vitalityScores = totalVitScores.map(score => score / totalVitWeight);
    }
  },

  /**
   * Sets the loading state of a Vitality Category
   */
  [types.SET_DATA_LOADING](state, payload) {
    if (payload.category && payload.loadingState !== undefined) {
      if (payload.category instanceof Array) {
        payload.category.forEach((category, i) => {
          updateRefreshState(state, category.key, payload.loadingState);
        });
      } else if (payload.category instanceof Object) {
        updateRefreshState(state, payload.category.key, payload.loadingState);
      }
    }
  },

  /** Update a loading information about a group */
  [types.SET_GROUP_LOADING](state, payload) {
    setGroupLoadingData(state, payload);
  },

  /**
   * Sets whether the fullscreen loading overlay should be displayed
   * @param {Boolean} payload - true if the overlay should be displayed
   */
  [types.DISPLAY_FULLSCREEN_LOADING](state, payload) {
    state.displayFullscreenLoading = payload;
  },

  /**
   * Sets whether the data is now finalizing
   * @param {Boolean} payload - true if the loading is finalizing, false if done or cancelled
   */
  [types.SET_DATA_FINALIZING](state, payload) {
    state.finalizingLoading = payload;
  },


  /**
   * Sets whether the sensei hints should be displayed for one or multiple criteria
   * @param {Object} payload - { show: true/false, criteria: VitalityCriterion or array of VitalityCriterion }
   */
  [types.SHOW_SENSEI_CRITERIA](state, payload) {
    const { show, criteria } = payload;
    if (criteria instanceof Array) {
      criteria.forEach((crit) => {
        state.showSenseiForCriterion[crit.key] = show;
        const lsk = `${LSK_HIDE_VH_CRITERION_SENSEI}${crit.key}`;
        if (show) {
          window.localStorage.removeItem(lsk);
        } else {
          window.localStorage.setItem(lsk, 'true');
        }
      });
    } else if (criteria.key) {
      state.showSenseiForCriterion[criteria.key] = show;
      const lsk = `${LSK_HIDE_VH_CRITERION_SENSEI}${criteria.key}`;
      if (show) {
        window.localStorage.removeItem(lsk);
      } else {
        window.localStorage.setItem(lsk, 'true');
      }
    }
  },


  /**
   * Sets whether the welcome message should be hidden or not
   * @param {Boolean} payload - true to show the welcome message, false to hide it
   */
  [types.SHOW_WELCOME](state, payload) {
    const hide = !payload;
    if (hide) {
      window.localStorage.setItem(LSK_HIDE_VH_WELCOME, 'true');
    } else {
      window.localStorage.removeItem(LSK_HIDE_VH_WELCOME);
    }
    state.showWelcomeMessage = !hide;
  },

};

export default mutations;
